// BilletterieLive.jsx — Mode LIVE branché au backend /api/billetterie/*
// Composant dédié au mode live (event_id présent). Pas de tabs démo (salon, admin, scan, cashless, sponsors).
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './Billetterie.css';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const COLOR_TO_PALETTE = {
  gold:   { color: 'var(--gold)',   chip: 'chip-gold',   icon: '⭐' },
  purple: { color: 'var(--purple)', chip: 'chip-purple', icon: '🤝' },
  blue:   { color: 'var(--blue)',   chip: 'chip-blue',   icon: '🪑' },
  green:  { color: 'var(--green)',  chip: 'chip-ok',     icon: '🕺' },
  cyan:   { color: 'var(--cyan)',   chip: 'chip-cyan',   icon: '🎟️' },
};

const paletteOf = (color) => COLOR_TO_PALETTE[color] || COLOR_TO_PALETTE.gold;

export default function BilletterieLive({ eventId }) {
  const [event, setEvent] = useState(null);
  const [soldSeats, setSoldSeats] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedSeat, setSelectedSeat] = useState(null); // {id, zoneId, tarifs[], standing}
  const [tarifId, setTarifId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [billetName, setBilletName] = useState('');
  const [billetEmail, setBilletEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [reservation, setReservation] = useState(null); // {billet_id, qr_code, lock_until}
  const [lockRemaining, setLockRemaining] = useState(0);
  const [success, setSuccess] = useState(null); // {qr_code, evt_nom}

  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);

  // ── Fonts ──
  useEffect(() => {
    const id = 'billetterie-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,700;0,900;1,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // ── Fetch event ──
  const reloadEvent = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/billetterie/event/${eventId}`);
      setEvent(data);
      setSoldSeats(new Set((data.siegesOccupes || []).map((s) => s.siege).filter(Boolean)));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Événement introuvable');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadEvent();
    const itv = setInterval(reloadEvent, 30000);
    return () => clearInterval(itv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // ── Timer du lock ──
  useEffect(() => {
    if (!reservation?.lock_until) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(reservation.lock_until) - new Date()) / 1000));
      setLockRemaining(remaining);
      if (remaining === 0) {
        setReservation(null);
        toast('⏰ Réservation expirée — sélectionnez à nouveau');
        reloadEvent();
      }
    };
    tick();
    const itv = setInterval(tick, 1000);
    return () => clearInterval(itv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation]);

  // ── Toast ──
  const toast = (msg) => {
    const id = ++toastId.current;
    setToasts((t) => [...t, { id, msg, show: false }]);
    requestAnimationFrame(() => requestAnimationFrame(() => setToasts((t) => t.map((x) => x.id === id ? { ...x, show: true } : x))));
    setTimeout(() => {
      setToasts((t) => t.map((x) => x.id === id ? { ...x, show: false } : x));
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 400);
    }, 3200);
  };

  // ── Zones dérivées ──
  const zones = useMemo(() => event?.plan_salle?.zones || [], [event]);
  const zonesById = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  const billetsVendus = event?.billets_vendus || 0;
  // Capacité totale : `event.capacite` si défini, sinon somme des zones du plan de salle
  // (zones debout : `places` ; zones assises : nb de sièges des rangées).
  const capaciteFromZones = useMemo(() => zones.reduce((sum, z) => {
    if (z.standing) return sum + (Number(z.places) || 80);
    return sum + (z.rangees || []).reduce((s, r) => s + (r.seats?.length || 0), 0);
  }, 0), [zones]);
  const capacite = event?.capacite || capaciteFromZones;
  const placesRestantes = Math.max(0, capacite - billetsVendus);

  // ── Sélection siège ──
  const selectSeat = (id, zoneId) => {
    if (soldSeats.has(id)) return;
    const z = zonesById[zoneId];
    if (!z) return;
    const tarifs = z.tarifs && z.tarifs.length ? z.tarifs : [
      { id: 'public', label: 'Plein tarif', prix: z.prix_public || 0, quota: null, default: true },
      { id: 'membre', label: 'Adhérent', prix: z.prix_membre || 0, quota: null, default: false },
    ];
    setSelectedSeat({ id, zoneId, tarifs, standing: false });
    const def = tarifs.find((t) => t.default) || tarifs[0];
    setTarifId(def.id);
  };

  const selectStanding = (zoneId) => {
    const z = zonesById[zoneId];
    if (!z) return;
    const tarifs = z.tarifs && z.tarifs.length ? z.tarifs : [
      { id: 'public', label: 'Plein tarif', prix: z.prix_public || 0, quota: null, default: true },
      { id: 'membre', label: 'Adhérent', prix: z.prix_membre || 0, quota: null, default: false },
    ];
    setSelectedSeat({ id: 'Fosse', zoneId, tarifs, standing: true });
    const def = tarifs.find((t) => t.default) || tarifs[0];
    setTarifId(def.id);
  };

  const currentTarif = selectedSeat?.tarifs?.find((t) => t.id === tarifId) || null;
  const currentPrice = currentTarif?.prix ?? 0;

  // Calcule les places restantes pour un tarif (si quota défini)
  const tarifRestants = (zone, tarif) => {
    if (tarif.quota == null) return null;
    const vendus = zone.stats?.parTarif?.[tarif.id] || 0;
    return Math.max(0, tarif.quota - vendus);
  };

  // ── Achat ──
  const openModal = () => {
    if (!selectedSeat) { toast('⚠️ Sélectionnez d\'abord une place'); return; }
    setBilletName(''); setBilletEmail(''); setModalOpen(true);
  };

  const acheter = async () => {
    if (!billetName || !billetEmail) { toast('⚠️ Merci de remplir votre nom et email'); return; }
    setSubmitting(true);
    try {
      const { data: rsv } = await axios.post(`${API_BASE}/billetterie/reserver`, {
        evenement_id: event.id,
        siege: selectedSeat.standing ? null : selectedSeat.id,
        zone: selectedSeat.zoneId,
        tarif_id: tarifId,
        nom_acheteur: billetName,
        email_acheteur: billetEmail,
      });
      setReservation(rsv);

      // Paiement en ligne (compte Stripe de l'asso) si activé et billet payant
      if (event.paiement_en_ligne && (rsv.prix || 0) > 0) {
        const { data: co } = await axios.post(`${API_BASE}/billetterie/checkout/${rsv.billet_id}`, {
          origin_url: window.location.origin,
        });
        if (co.checkout_url) { window.location.href = co.checkout_url; return; }
      }

      // Billet gratuit ou règlement hors-ligne : confirmation directe
      const { data: cnf } = await axios.post(`${API_BASE}/billetterie/confirmer/${rsv.billet_id}`, {});
      setSubmitting(false);
      setModalOpen(false);
      setReservation(null);
      setSuccess({ qr_code: cnf.billet.qr_code, evt_nom: event.nom });
      setSelectedSeat(null);
      reloadEvent();
    } catch (err) {
      setSubmitting(false);
      toast(`❌ ${err?.response?.data?.detail || err?.response?.data?.error || 'Erreur réservation'}`);
    }
  };

  // ── RENDERS ──
  const renderSeat = (zoneId, row, seat) => {
    if (seat === '|') return <div className="aisle" key={`${row}-|-${Math.random()}`}></div>;
    const id = `${row}-${seat}`;
    const isSold = soldSeats.has(id);
    const isSelected = selectedSeat?.id === id;
    const cls = zoneId === 'VIP' ? 'vip' : zoneId === 'Partenaire' ? 'partner' : 'standard';
    const letter = zoneId === 'VIP' ? 'V' : zoneId === 'Partenaire' ? 'P' : 'S';
    return (
      <div
        key={id}
        className={`seat ${cls}${isSold ? ' sold' : ''}${isSelected ? ' selected-seat' : ''}`}
        onClick={() => selectSeat(id, zoneId)}
        role="button"
        tabIndex={isSold ? -1 : 0}
        aria-disabled={isSold ? 'true' : 'false'}
        aria-label={isSold ? `Siège ${id} — vendu` : `Siège ${id} — disponible`}
        title={isSold ? `${id} — Vendu` : id}
        data-testid={`seat-${id}`}
        data-sold={isSold ? 'true' : 'false'}
      >{isSold ? '' : letter}</div>
    );
  };

  // ── État de chargement ──
  if (loading) {
    return (
      <div className="billetterie-root" data-testid="billetterie-root">
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 28px', display: 'flex', alignItems: 'center', gap: '14px', fontSize: '14px', color: 'var(--muted)' }} data-testid="live-loading">
            <span className="live-dot" style={{ width: 10, height: 10 }}></span>
            Chargement de l'événement…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billetterie-root" data-testid="billetterie-root">
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', gap: '14px' }}>
          <div style={{ fontSize: '64px' }}>❌</div>
          <div style={{ background: 'rgba(255,92,106,0.08)', border: '1px solid rgba(255,92,106,0.3)', borderRadius: '14px', padding: '16px 22px', fontSize: '14px', color: '#ff5c6a', maxWidth: '480px', textAlign: 'center' }} data-testid="live-error">
            {error}
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}>
              Vérifiez le lien <code style={{ fontFamily: 'monospace' }}>?event_id=…</code> ou demandez-le à votre association.
            </div>
          </div>
          <Link to="/" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>← Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="billetterie-root" data-testid="billetterie-root">
      {/* TOASTS */}
      <div className="toast-wrap">
        {toasts.map((t) => <div key={t.id} className={'toast' + (t.show ? ' show' : '')}>{t.msg}</div>)}
      </div>

      {/* NAV simplifié */}
      <div className="main-tabs">
        <div className="tabs-logo">🎵 <span>{event.asso_nom}</span> Billetterie</div>
        <div className="tabs-center" />
        <div className="tabs-right">
          <Link to="/" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>← AssoPerf</Link>
        </div>
      </div>

      {/* BANDEAU LIVE */}
      <div style={{ paddingTop: '62px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '16px 24px 0' }}>
          <div style={{ background: 'linear-gradient(90deg, rgba(34,211,160,0.06), rgba(34,211,160,0))', border: '1px solid rgba(34,211,160,0.3)', borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', flexWrap: 'wrap' }} data-testid="live-active">
            <span className="live-dot"></span>
            <strong style={{ color: 'var(--green)' }}>Mode LIVE</strong>
            <span style={{ color: 'var(--muted)' }}>·</span>
            <span><strong>{event.asso_nom}</strong></span>
            <span style={{ color: 'var(--muted)' }}>·</span>
            <span style={{ color: 'var(--muted)' }}>{billetsVendus} billet{billetsVendus > 1 ? 's' : ''} vendu{billetsVendus > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* HERO */}
        <div className="billetterie-hero-section" style={{ background: 'linear-gradient(180deg,#0a0f18,#05080a)', padding: '40px 32px 30px', borderBottom: '1px solid var(--border)', marginTop: '16px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div className="billetterie-hero-row">
              <div className="billetterie-hero-img">🎫</div>
              <div className="billetterie-hero-content">
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <span className="chip chip-ok">🟢 Billetterie ouverte</span>
                  <span className="chip chip-cyan">📱 QR code + Cashless</span>
                </div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: 'clamp(24px,4vw,48px)', fontWeight: 900, lineHeight: 1.1, marginBottom: '10px' }} data-testid="event-title">
                  {event.nom}<br /><span style={{ color: 'var(--gold)' }}>{new Date(event.date_debut).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                </div>
                <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '6px' }} data-testid="event-lieu">📍 {event.lieu}</div>
                <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '16px' }}>🕗 {new Date(event.date_debut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                <div className="billetterie-stats-row">
                  <div className="billetterie-stat-card">
                    <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--gold)' }} data-testid="places-restantes">{placesRestantes}</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Places restantes</div>
                  </div>
                  <div className="billetterie-stat-card">
                    <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--green)' }} data-testid="billets-vendus">{billetsVendus}</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Billets vendus</div>
                  </div>
                  <div className="billetterie-stat-card">
                    <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--cyan)' }} data-testid="capacite">{capacite}</div>
                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Capacité totale</div>
                  </div>
                </div>
              </div>
              <div className="billetterie-hero-tarifs" style={{ background: 'var(--surface)', border: '1px solid var(--gold-border)', borderRadius: '16px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '14px', color: 'var(--gold)' }}>🎫 Tarifs proposés</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }} data-testid="tarifs-panel">
                  {zones.map((z) => {
                    const p = paletteOf(z.color);
                    const zTarifs = z.tarifs || [];
                    const minPrix = zTarifs.length ? Math.min(...zTarifs.map((t) => t.prix)) : 0;
                    const maxPrix = zTarifs.length ? Math.max(...zTarifs.map((t) => t.prix)) : 0;
                    return (
                      <div key={z.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                        <span style={{ fontWeight: 700 }}>{(z.standing ? '🕺' : p.icon) + ' ' + z.id}</span>
                        <span style={{ fontWeight: 800, color: p.color }}>{minPrix === maxPrix ? `${minPrix}€` : `${minPrix}–${maxPrix}€`}</span>
                      </div>
                    );
                  })}
                  <div style={{ fontSize: '11px', color: 'var(--green)', padding: '4px 0', textAlign: 'center' }}>🎁 Plusieurs tarifs disponibles à la sélection</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PLAN DE SALLE + PANIER */}
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '28px 24px' }}>
          <div className="g2" style={{ gap: '24px', alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 800, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🗺️ Plan de salle interactif
                <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--muted)' }}>Cliquez sur une place</span>
              </div>
              <div className="salle-container">
                <div className="scene-band">🎵 Scène 🎵</div>
                {zones.map((z) => {
                  const p = paletteOf(z.color);
                  const zTarifs = z.tarifs || [];
                  const minPrix = zTarifs.length ? Math.min(...zTarifs.map((t) => t.prix)) : (z.prix_public || 0);
                  const maxPrix = zTarifs.length ? Math.max(...zTarifs.map((t) => t.prix)) : (z.prix_public || 0);
                  const prixLabel = minPrix === maxPrix ? `${minPrix}€` : `de ${minPrix}€ à ${maxPrix}€`;
                  if (z.standing) {
                    const placesMax = z.places || 80;
                    const debutVendus = event.debout_vendus || 0;
                    return (
                      <div key={z.id}>
                        <div className="zone-header" style={{ color: p.color }}>{p.icon} {z.label || `Zone ${z.id}`} — {prixLabel}</div>
                        <div className={'standing-area' + (selectedSeat?.standing && selectedSeat?.zoneId === z.id ? ' active' : '')} onClick={() => selectStanding(z.id)} data-testid={`standing-${z.id}`}>
                          <div style={{ fontSize: '22px', marginBottom: '6px' }}>🕺💃</div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: p.color }}>{z.label || `Zone ${z.id}`}</div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>{Math.max(0, placesMax - debutVendus)} places disponibles sur {placesMax}</div>
                          <div style={{ fontSize: '11px', color: p.color, marginTop: '6px' }}>Cliquez pour sélectionner</div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={z.id}>
                      <div className="zone-header" style={{ color: p.color }}>{p.icon} {z.label || `Zone ${z.id}`} — {prixLabel}</div>
                      <div className="seats-grid">
                        {(z.rangees || []).map((r) => (
                          <div className="seat-row" key={`${z.id}-${r.row}`}>
                            <div className="row-label" style={{ color: p.color }}>{r.row}</div>
                            {r.seats.map((seat) => renderSeat(z.id, r.row, seat))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <div className="seat-legend" style={{ marginTop: '14px' }}>
                  {zones.filter((z) => !z.standing).map((z) => {
                    const p = paletteOf(z.color);
                    return <div key={z.id} className="leg"><div className="leg-dot" style={{ background: p.color, opacity: 0.7 }}></div>{z.id}</div>;
                  })}
                  <div className="leg"><div className="leg-dot" style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }}></div>Vendu</div>
                </div>
              </div>
            </div>

            {/* PANIER */}
            <div>
              <div className="card" style={{ marginBottom: '14px', borderColor: 'var(--gold-border)' }}>
                <div className="card-hdr" style={{ borderBottomColor: 'var(--gold-border)' }}>
                  <div className="card-title" style={{ color: 'var(--gold)' }}>🛒 Votre sélection</div>
                </div>
                <div className="card-body">
                  {!selectedSeat ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)', fontSize: '13px' }} data-testid="panier-empty">
                      <div style={{ fontSize: '28px', marginBottom: '8px' }}>👆</div>
                      Cliquez sur une place pour la sélectionner
                    </div>
                  ) : (
                    <div data-testid="panier-content">
                      <div style={{ background: 'var(--surface2)', borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700 }} data-testid="panier-seat">
                            {selectedSeat.standing ? `Zone ${selectedSeat.zoneId} — Fosse` : `Siège ${selectedSeat.id} — Zone ${selectedSeat.zoneId}`}
                          </div>
                          <span className={'chip ' + paletteOf(zonesById[selectedSeat.zoneId]?.color).chip}>{selectedSeat.zoneId}</span>
                        </div>
                        <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '6px' }}>Choisissez votre tarif</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }} data-testid="tarif-options">
                          {(selectedSeat.tarifs || []).map((t) => {
                            const isActive = tarifId === t.id;
                            const restants = tarifRestants(zonesById[selectedSeat.zoneId] || {}, t);
                            const epuise = restants === 0;
                            return (
                              <div
                                key={t.id}
                                onClick={() => !epuise && setTarifId(t.id)}
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: '8px',
                                  background: isActive ? 'var(--gold-soft)' : 'var(--surface2)',
                                  border: isActive ? '2px solid var(--gold)' : '2px solid var(--border)',
                                  cursor: epuise ? 'not-allowed' : 'pointer',
                                  opacity: epuise ? 0.5 : 1,
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                }}
                                data-testid={`tarif-${t.id}`}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: isActive ? 'var(--gold)' : 'var(--text)' }}>
                                    {t.label}
                                  </span>
                                  {t.quota != null && (
                                    <span style={{ fontSize: '10px', color: epuise ? '#ff5c6a' : 'var(--muted)' }}>
                                      {epuise ? 'Épuisé' : `${restants ?? '—'} place${restants > 1 ? 's' : ''} restante${restants > 1 ? 's' : ''}`}
                                    </span>
                                  )}
                                </div>
                                <span style={{ fontSize: '16px', fontWeight: 800, color: isActive ? 'var(--gold)' : 'var(--text)' }}>{t.prix}€</span>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                          <span style={{ color: 'var(--muted)' }}>Total</span>
                          <strong style={{ color: 'var(--gold)', fontSize: '16px' }} data-testid="panier-total">{currentPrice}€</strong>
                        </div>
                      </div>
                      <button className="btn btn-gold btn-full" onClick={openModal} data-testid="btn-reserver" disabled={!tarifId}>🎫 Réserver ce billet →</button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'center', padding: '10px' }}>
                💳 Paiement sécurisé Stripe · 🎫 Billet QR envoyé par email instantanément
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL ACHAT */}
      <div className={'modal-bg' + (modalOpen ? ' open' : '')} onClick={(e) => e.target === e.currentTarget && setModalOpen(false)} data-testid="modal-billet">
        <div className="modal">
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎫</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: '20px', fontWeight: 900, marginBottom: '4px' }} data-testid="modal-event-name">{event.nom}</div>
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>{event.lieu}</div>
            {reservation && lockRemaining > 0 && (
              <div style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: 'rgba(255,173,77,0.1)', border: '1px solid rgba(255,173,77,0.3)', borderRadius: '999px', fontSize: '12px', color: '#ffad4d' }} data-testid="lock-timer">
                ⏳ Réservation verrouillée : {Math.floor(lockRemaining / 60)}:{String(lockRemaining % 60).padStart(2, '0')}
              </div>
            )}
          </div>
          <div style={{ background: 'var(--surface2)', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700 }}>
                {selectedSeat && (selectedSeat.standing ? `Zone ${selectedSeat.zoneId}` : `Siège ${selectedSeat.id}`)}
              </div>
              {selectedSeat && <span className={'chip ' + paletteOf(zonesById[selectedSeat.zoneId]?.color).chip}>{selectedSeat.zoneId}</span>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--muted)' }}>
              <span>Tarif sélectionné</span>
              <strong style={{ color: 'var(--gold)', fontSize: '16px' }}>{currentPrice}€</strong>
            </div>
          </div>
          <div className="fg"><label className="fl">Prénom & Nom</label><input className="fi" placeholder="Marie Dupont" value={billetName} onChange={(e) => setBilletName(e.target.value)} data-testid="billet-name" /></div>
          <div className="fg"><label className="fl">Email (reçoit le billet QR)</label><input className="fi" type="email" placeholder="marie@email.fr" value={billetEmail} onChange={(e) => setBilletEmail(e.target.value)} data-testid="billet-email" /></div>
          <div style={{ background: 'var(--gold-soft)', border: '1px solid var(--gold-border)', borderRadius: '10px', padding: '12px', marginBottom: '14px', fontSize: '12px', color: 'var(--muted)' }}>
            🎫 Votre billet avec QR code sera disponible immédiatement.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setModalOpen(false)}>Annuler</button>
            <button className="btn btn-gold" style={{ flex: 2 }} onClick={acheter} disabled={submitting} data-testid="btn-payer">{submitting ? '⏳ Traitement…' : `🔒 Payer ${currentPrice}€ →`}</button>
          </div>
          <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--muted)', marginTop: '10px' }}>Paiement sécurisé Stripe · Remboursable 48h avant</div>
        </div>
      </div>

      {/* MODAL SUCCÈS */}
      <div className={'modal-bg' + (success ? ' open' : '')} onClick={(e) => e.target === e.currentTarget && setSuccess(null)} data-testid="modal-success">
        {success && (
          <div className="modal" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '56px', marginBottom: '14px' }}>🎫</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: '22px', fontWeight: 900, marginBottom: '8px' }}>Billet confirmé !</div>
            <div style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: 1.7, marginBottom: '22px' }}>
              Votre billet QR est prêt. Présentez-le à l'entrée de {event.lieu || 'l\'événement'}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a
                href={`/billet/${success.qr_code}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-gold btn-full btn-lg"
                style={{ textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}
                data-testid="btn-voir-billet"
              >🎫 Voir mon billet (QR) →</a>
              <button className="btn btn-ghost btn-full" onClick={() => setSuccess(null)} data-testid="btn-success-close">Fermer</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
